import React, { useEffect, useState } from 'react';
import FormControl, { SearchSelect } from '../common/FormControl';
import auditPlanService, {
  PLAN_TIMELINES, PlanPeriodicity, PlanYearType, ReportingPeriod,
} from '../../services/auditPlanService';

/**
 * The agency's audit-plan window (BRs APM-5.1, APM-5.2), shared by the create
 * and configure agency modals: the timeline, the year it reports in (calendar
 * or financial), a mandatory start period and an optional end period. Periods
 * are periods UNDER AUDIT — a Q1 audit may be carried out in Q2.
 */

export interface PlanWindowValue {
  periodicity: PlanPeriodicity;
  yearType: PlanYearType;
  startId: number | null;
  endId: number | null;
}

interface Props {
  value: PlanWindowValue;
  onChange: (next: PlanWindowValue) => void;
  disabled?: boolean;
  testIdPrefix: string;
}

const AgencyPlanWindowFields: React.FC<Props> = ({ value, onChange, disabled, testIdPrefix }) => {
  const [periods, setPeriods] = useState<ReportingPeriod[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    auditPlanService.getPeriods(value.yearType, value.periodicity)
      .then(rows => { if (!cancelled) setPeriods(rows); })
      .catch(() => { if (!cancelled) setPeriods([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [value.yearType, value.periodicity]);

  const start = periods.find(p => p.id === value.startId);
  const options = periods.map(p => ({ value: p.id, label: p.label }));
  const endOptions = periods
    .filter(p => !start || p.start_date >= start.start_date)
    .map(p => ({ value: p.id, label: p.label }));

  // A start or end that belongs to another timeline / year type is cleared:
  // the server refuses it, so the form must not keep showing it.
  const setShape = (patch: Partial<PlanWindowValue>) =>
    onChange({ ...value, ...patch, startId: null, endId: null });

  return (
    <>
      <div className="grid grid-cols-2 gap-x-4">
        <FormControl.Select
          label="Audit plan timeline"
          value={value.periodicity}
          onChange={e => setShape({ periodicity: e.target.value as PlanPeriodicity })}
          disabled={disabled}
          data-testid={`${testIdPrefix}_plan_periodicity`}
        >
          {PLAN_TIMELINES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </FormControl.Select>
        <FormControl.Select
          label="Year type"
          value={value.yearType}
          onChange={e => setShape({ yearType: e.target.value as PlanYearType })}
          disabled={disabled}
          data-testid={`${testIdPrefix}_plan_year_type`}
        >
          <option value="FINANCIAL">Financial year</option>
          <option value="CALENDAR">Calendar year</option>
        </FormControl.Select>
      </div>
      <div className="grid grid-cols-2 gap-x-4">
        <SearchSelect
          label="Start period"
          required
          options={options}
          value={value.startId}
          onChange={v => {
            const id = v == null ? null : Number(v);
            const s = periods.find(p => p.id === id);
            const end = periods.find(p => p.id === value.endId);
            onChange({ ...value, startId: id, endId: end && s && end.start_date < s.start_date ? null : value.endId });
          }}
          placeholder={loading ? 'Loading…' : 'e.g. FY2025-26 Q3'}
          disabled={disabled}
          data-testid={`${testIdPrefix}_plan_start_period`}
        />
        <SearchSelect
          label="End period"
          options={endOptions}
          value={value.endId}
          onChange={v => onChange({ ...value, endId: v == null ? null : Number(v) })}
          clearable
          clearLabel="— Open-ended —"
          placeholder="Open-ended"
          disabled={disabled}
          data-testid={`${testIdPrefix}_plan_end_period`}
        />
      </div>
      <p className="text-xs text-gray-500" style={{ marginTop: -8 }}>
        Periods under audit — the columns of the agency’s Audit Plan screen. A Q1 audit may be carried out in Q2;
        it still belongs to Q1. Leave the end period empty for an open-ended appointment.
      </p>
    </>
  );
};

export default AgencyPlanWindowFields;
